import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { FixtureElement } from "../domain/types";

// Named entries keep the original D0 fixture palette exactly (Wall/Door/Slab/Beam/Proxy are its
// only element types). Real IFC sources expose many more IFC classes (Column, Covering, Fixture,
// Floor, ...); named colors are added for the common ones, but elementTypeColor()'s hashed
// fallback is what actually matters here — real-world type sets aren't fully known in advance, and
// every type previously not in this map collapsed onto the same flat gray-blue default, making a
// real building look like an undifferentiated mass instead of distinguishable per-type colors
// (kaz, 2026-08-11).
const materialColors: Record<string, number> = {
  Wall: 0x79a9ff,
  Door: 0xf3b562,
  Slab: 0x89c997,
  Beam: 0xc58af9,
  Proxy: 0x7b8496,
  Column: 0xe08a6e,
  Floor: 0xd9c88a,
  Covering: 0x6fb8c2,
  Fixture: 0xf2e07a,
  Window: 0xa8d8ff,
  Roof: 0xb5765a,
  Railing: 0x9fa8c9,
  Stair: 0xd68fc9,
  Space: 0x6b7488,
  Furnishing: 0x8fd68f
};

/**
 * A type not in `materialColors` gets a deterministic color derived from its own name (same
 * FNV-1a-style hash every run, not `Math.random()`) instead of one flat default — every distinct
 * type in a real IFC source stays visually distinguishable even though this file cannot name every
 * IFC class up front. Saturation/lightness match the hand-picked palette's muted-pastel range.
 */
function elementTypeColor(type: string): number {
  const named = materialColors[type];
  if (named !== undefined) return named;
  let hash = 0;
  for (let index = 0; index < type.length; index += 1) {
    hash = (hash * 31 + type.charCodeAt(index)) >>> 0;
  }
  return new THREE.Color().setHSL((hash % 360) / 360, 0.42, 0.62).getHex();
}
const cameraTransitionMs = 320;
const cameraTravelPerWheelStep = 0.14;
const selectDoublePressMs = 500;
const selectDoublePressRadius = 10;

// BoxGeometry and the tessellated BufferGeometry built for a MeshGeometry record both extend
// THREE.BufferGeometry, so one mesh type covers both renderable geometry kinds.
interface SelectableMesh extends THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial> {
  userData: { elementId: string };
}

interface CameraViewSnapshot {
  readonly camera: THREE.Vector3;
  readonly target: THREE.Vector3;
  readonly sceneSpan: number;
  readonly minDistance: number;
  readonly maxDistance: number;
}

interface FocusSample {
  readonly elementId: string;
  readonly point: THREE.Vector3;
}

export interface SceneSelectionModifiers {
  readonly additive: boolean;
}

export type SceneInteractionMode = "navigate" | "pan" | "select";

export class FixtureScene {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 200);
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly meshes = new Map<string, SelectableMesh>();
  private readonly meshBounds = new Map<string, THREE.Box3>();
  private readonly resizeObserver: ResizeObserver;
  private readonly controls: OrbitControls;
  private readonly selectionOverlay: HTMLDivElement;
  private readonly orbitPivot: HTMLDivElement;
  private readonly selectionOutline = new THREE.LineSegments(
    new THREE.BufferGeometry(),
    new THREE.LineBasicMaterial({ color: 0x00e6a8, depthTest: false, depthWrite: false, toneMapped: false })
  );
  private selectionOutlineCount = 0;
  private sceneSpan = 4;
  private transitionFrame: number | null = null;
  private transitioning = false;
  private previousFocusView: CameraViewSnapshot | null = null;
  private lastFocusVisibility = { visibleSamples: 0, totalSamples: 0, candidateCount: 0 };
  private interactionMode: SceneInteractionMode = "navigate";
  private windowSelectionStart: { readonly x: number; readonly y: number; readonly pointerId: number } | null = null;
  private selectPanStart: { x: number; y: number; readonly pointerId: number } | null = null;
  private lastSelectClick: { readonly x: number; readonly y: number; readonly at: number } | null = null;

  constructor(
    private readonly container: HTMLElement,
    elements: readonly FixtureElement[],
    private readonly onFocus: (id: string) => void,
    private readonly onRangeSelect: (ids: ReadonlySet<string>, modifiers: SceneSelectionModifiers) => void
  ) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.container.append(this.renderer.domElement);
    this.selectionOverlay = document.createElement("div");
    this.selectionOverlay.className = "scene-window-selection";
    this.selectionOverlay.hidden = true;
    this.container.append(this.selectionOverlay);
    this.orbitPivot = document.createElement("div");
    this.orbitPivot.className = "scene-orbit-pivot";
    this.orbitPivot.setAttribute("aria-hidden", "true");
    this.container.append(this.orbitPivot);
    this.scene.add(new THREE.HemisphereLight(0xf2f7ff, 0x20293a, 2.4));

    const directional = new THREE.DirectionalLight(0xffffff, 2.2);
    directional.position.set(18, 24, 12);
    this.scene.add(directional);
    this.selectionOutline.renderOrder = 10;
    this.selectionOutline.visible = false;
    this.scene.add(this.selectionOutline);

    this.camera.position.set(23, 20, 30);
    this.camera.lookAt(15, 0, 10);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = false;
    this.controls.screenSpacePanning = true;
    this.controls.zoomToCursor = true;
    this.controls.enableZoom = false;
    this.controls.zoomSpeed = 1.15;
    this.controls.rotateSpeed = 0.8;
    this.controls.panSpeed = 0.9;
    this.controls.minPolarAngle = 0.01;
    this.controls.maxPolarAngle = Math.PI - 0.01;
    this.controls.addEventListener("change", this.handleControlsChange);
    this.controls.addEventListener("start", this.cancelCameraTransition);
    this.setElements(elements);
    this.renderer.domElement.addEventListener("wheel", this.moveCameraThroughScene, { capture: true, passive: false });
    this.renderer.domElement.addEventListener("dblclick", this.focusPick);
    this.renderer.domElement.addEventListener("pointerdown", this.startWindowSelection, true);
    this.renderer.domElement.addEventListener("pointermove", this.moveWindowSelection, true);
    this.renderer.domElement.addEventListener("pointerup", this.finishWindowSelection, true);
    this.renderer.domElement.addEventListener("pointercancel", this.cancelWindowSelection);
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.container);
    this.resize();
  }

  setSelection(selectedIds: ReadonlySet<string>): void {
    this.updateSelectionOutline(selectedIds);
    this.render();
  }

  setInteractionMode(mode: SceneInteractionMode): void {
    this.cancelWindowSelection();
    this.lastSelectClick = null;
    this.interactionMode = mode;
    this.controls.enabled = true;
    this.controls.enableRotate = mode === "navigate";
    this.controls.enablePan = true;
    this.controls.mouseButtons.LEFT = mode === "navigate" ? THREE.MOUSE.ROTATE : THREE.MOUSE.PAN;
    this.controls.mouseButtons.MIDDLE = THREE.MOUSE.DOLLY;
    this.controls.mouseButtons.RIGHT = THREE.MOUSE.PAN;
    this.renderer.domElement.style.cursor = mode === "select" ? "crosshair" : "grab";
    this.orbitPivot.hidden = mode !== "navigate" || this.meshes.size === 0;
    this.container.dataset.interactionMode = mode;
  }

  setElements(elements: readonly FixtureElement[]): void {
    this.cancelCameraTransition();
    this.previousFocusView = null;
    this.lastFocusVisibility = { visibleSamples: 0, totalSamples: 0, candidateCount: 0 };
    this.clearElements();
    this.addElements(elements.slice(0, 5_000));
    this.orbitPivot.hidden = this.interactionMode !== "navigate" || this.meshes.size === 0;
    this.frameElements(false);
    this.render();
  }

  focusElements(elementIds: ReadonlySet<string>): boolean {
    const bounds = new THREE.Box3();
    let matched = 0;
    for (const id of elementIds) {
      const mesh = this.meshes.get(id);
      if (mesh === undefined) continue;
      bounds.expandByObject(mesh);
      matched += 1;
    }
    if (matched === 0) return false;
    this.previousFocusView = {
      camera: this.camera.position.clone(),
      target: this.controls.target.clone(),
      sceneSpan: this.sceneSpan,
      minDistance: this.controls.minDistance,
      maxDistance: this.controls.maxDistance
    };
    const samples = this.focusSamples(elementIds);
    const focus = this.leastOccludedFocusDirection(bounds, samples);
    this.lastFocusVisibility = {
      visibleSamples: focus.visibleSamples,
      totalSamples: samples.length,
      candidateCount: focus.candidateCount
    };
    this.frameBounds(bounds, focus.direction, true);
    return true;
  }

  frameAll(): boolean {
    if (this.meshes.size === 0) return false;
    this.previousFocusView = null;
    this.lastFocusVisibility = { visibleSamples: 0, totalSamples: 0, candidateCount: 0 };
    this.frameElements(true);
    return true;
  }

  restorePreviousView(): boolean {
    const previous = this.previousFocusView;
    if (previous === null) return false;
    this.previousFocusView = null;
    this.lastFocusVisibility = { visibleSamples: 0, totalSamples: 0, candidateCount: 0 };
    this.sceneSpan = previous.sceneSpan;
    this.controls.minDistance = previous.minDistance;
    this.controls.maxDistance = previous.maxDistance;
    this.moveToView(previous.camera, previous.target, true);
    return true;
  }

  viewState(): {
    readonly camera: readonly [number, number, number];
    readonly target: readonly [number, number, number];
    readonly renderable: number;
    readonly sceneSpan: number;
    readonly orbitPivotVisible: boolean;
    readonly zoomToCursor: boolean;
    readonly transitioning: boolean;
    readonly interactionMode: SceneInteractionMode;
    readonly selectionOutlines: number;
    readonly canRestoreView: boolean;
    readonly focusVisibleSamples: number;
    readonly focusSampleCount: number;
    readonly focusCandidateCount: number;
  } {
    return {
      camera: [this.camera.position.x, this.camera.position.y, this.camera.position.z],
      target: [this.controls.target.x, this.controls.target.y, this.controls.target.z],
      renderable: this.meshes.size,
      sceneSpan: this.sceneSpan,
      orbitPivotVisible: !this.orbitPivot.hidden,
      zoomToCursor: this.controls.zoomToCursor,
      transitioning: this.transitioning,
      interactionMode: this.interactionMode,
      selectionOutlines: this.selectionOutlineCount,
      canRestoreView: this.previousFocusView !== null,
      focusVisibleSamples: this.lastFocusVisibility.visibleSamples,
      focusSampleCount: this.lastFocusVisibility.totalSamples,
      focusCandidateCount: this.lastFocusVisibility.candidateCount
    };
  }

  dispose(): void {
    this.cancelCameraTransition();
    this.resizeObserver.disconnect();
    this.renderer.domElement.removeEventListener("wheel", this.moveCameraThroughScene, true);
    this.renderer.domElement.removeEventListener("dblclick", this.focusPick);
    this.renderer.domElement.removeEventListener("pointerdown", this.startWindowSelection, true);
    this.renderer.domElement.removeEventListener("pointermove", this.moveWindowSelection, true);
    this.renderer.domElement.removeEventListener("pointerup", this.finishWindowSelection, true);
    this.renderer.domElement.removeEventListener("pointercancel", this.cancelWindowSelection);
    this.controls.removeEventListener("change", this.handleControlsChange);
    this.controls.removeEventListener("start", this.cancelCameraTransition);
    this.controls.dispose();
    this.orbitPivot.remove();
    this.selectionOverlay.remove();
    this.clearElements();
    this.scene.remove(this.selectionOutline);
    this.selectionOutline.geometry.dispose();
    this.selectionOutline.material.dispose();
    this.renderer.dispose();
  }

  private addElements(elements: readonly FixtureElement[]): void {
    for (const element of elements) {
      if (element.geometry === null) continue;
      const color = elementTypeColor(element.type);
      const mesh: SelectableMesh =
        element.geometry.kind === "mesh"
          ? this.buildMeshGeometryMesh(element.geometry, color)
          : this.buildBoxGeometryMesh(element.geometry, color);
      mesh.userData = { elementId: element.id };
      mesh.updateWorldMatrix(true, false);
      this.meshes.set(element.id, mesh);
      this.meshBounds.set(element.id, new THREE.Box3().setFromObject(mesh));
      this.scene.add(mesh);
    }
  }

  private buildBoxGeometryMesh(geometry: Extract<FixtureElement["geometry"], { kind: "box" }>, color: number): SelectableMesh {
    const [width, height, depth] = geometry.size;
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(width, height, depth),
      new THREE.MeshStandardMaterial({ color, roughness: 0.64, metalness: 0.04 })
    ) as unknown as SelectableMesh;
    const [x, y, z] = geometry.position;
    mesh.position.set(x, y + height / 2, z);
    mesh.rotation.y = geometry.rotationY ?? 0;
    return mesh;
  }

  // Tessellated mesh vertex positions are already absolute world-space metres (see
  // src/domain/types.ts's MeshGeometry doc comment), so unlike a box this mesh keeps an identity
  // transform -- no position/rotation is applied here.
  private buildMeshGeometryMesh(geometry: Extract<FixtureElement["geometry"], { kind: "mesh" }>, color: number): SelectableMesh {
    const bufferGeometry = new THREE.BufferGeometry();
    bufferGeometry.setAttribute("position", new THREE.BufferAttribute(geometry.vertexPositions, 3));
    bufferGeometry.setIndex(new THREE.BufferAttribute(geometry.triangleIndices, 1));
    bufferGeometry.computeVertexNormals();
    bufferGeometry.computeBoundingBox();
    return new THREE.Mesh(
      bufferGeometry,
      new THREE.MeshStandardMaterial({ color, roughness: 0.64, metalness: 0.04, side: THREE.DoubleSide })
    ) as unknown as SelectableMesh;
  }

  private clearElements(): void {
    this.updateSelectionOutline(new Set());
    for (const mesh of this.meshes.values()) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      mesh.material.dispose();
    }
    this.meshes.clear();
    this.meshBounds.clear();
  }

  private updateSelectionOutline(selectedIds: ReadonlySet<string>): void {
    const positions: number[] = [];
    const corner = new THREE.Vector3();
    let outlined = 0;
    const edgeIndices = [
      0, 1, 1, 3, 3, 2, 2, 0,
      4, 5, 5, 7, 7, 6, 6, 4,
      0, 4, 1, 5, 2, 6, 3, 7
    ] as const;
    for (const id of selectedIds) {
      const mesh = this.meshes.get(id);
      if (mesh === undefined) continue;
      if (mesh.geometry.boundingBox === null) mesh.geometry.computeBoundingBox();
      const bounds = mesh.geometry.boundingBox;
      if (bounds === null) continue;
      mesh.updateWorldMatrix(true, false);
      const { min, max } = bounds;
      const corners = [
        [min.x, min.y, min.z], [max.x, min.y, min.z], [min.x, max.y, min.z], [max.x, max.y, min.z],
        [min.x, min.y, max.z], [max.x, min.y, max.z], [min.x, max.y, max.z], [max.x, max.y, max.z]
      ] as const;
      for (const index of edgeIndices) {
        corner.set(...corners[index]).applyMatrix4(mesh.matrixWorld);
        positions.push(corner.x, corner.y, corner.z);
      }
      outlined += 1;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    this.selectionOutline.geometry.dispose();
    this.selectionOutline.geometry = geometry;
    this.selectionOutline.visible = outlined > 0;
    this.selectionOutlineCount = outlined;
  }

  private frameElements(animate: boolean): void {
    if (this.meshes.size === 0) {
      this.camera.position.set(8, 7, 10);
      this.controls.target.set(0, 0, 0);
      this.controls.update();
      return;
    }
    const bounds = new THREE.Box3();
    for (const mesh of this.meshes.values()) bounds.expandByObject(mesh);
    this.frameBounds(bounds, new THREE.Vector3(0.85, 0.75, 1.1), animate);
  }

  private frameBounds(bounds: THREE.Box3, direction?: THREE.Vector3, animate = false): void {
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const span = Math.max(size.x, size.y, size.z, 4);
    this.sceneSpan = span;
    const viewDirection = direction ?? this.camera.position.clone().sub(this.controls.target).normalize();
    if (viewDirection.lengthSq() === 0) viewDirection.set(0.85, 0.75, 1.1);
    viewDirection.normalize();
    const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * Math.max(this.camera.aspect, 0.1));
    const fitFov = Math.max(0.1, Math.min(verticalFov, horizontalFov));
    const distance = Math.max(1, (span * 0.62) / Math.tan(fitFov / 2));
    const cameraPosition = center.clone().addScaledVector(viewDirection, distance);
    this.controls.minDistance = Math.max(0.02, span / 20_000);
    this.controls.maxDistance = Math.max(100, span * 50);
    this.moveToView(cameraPosition, center, animate);
  }

  private moveToView(cameraPosition: THREE.Vector3, target: THREE.Vector3, animate: boolean): void {
    if (animate && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.transitionCamera(cameraPosition, target);
      return;
    }
    this.cancelCameraTransition();
    this.controls.target.copy(target);
    this.camera.position.copy(cameraPosition);
    this.updateClipping();
    this.controls.update();
    this.render();
  }

  private focusSamples(elementIds: ReadonlySet<string>): FocusSample[] {
    const samples: FocusSample[] = [];
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    for (const id of [...elementIds].slice(0, 4)) {
      const bounds = this.meshBounds.get(id);
      if (bounds === undefined) continue;
      bounds.getCenter(center);
      bounds.getSize(size);
      samples.push({ elementId: id, point: center.clone() });
      for (const [x, y, z] of [
        [0.32, 0, 0], [-0.32, 0, 0], [0, 0.32, 0], [0, -0.32, 0], [0, 0, 0.32], [0, 0, -0.32]
      ] as const) {
        samples.push({
          elementId: id,
          point: center.clone().add(new THREE.Vector3(size.x * x, size.y * y, size.z * z))
        });
      }
    }
    return samples;
  }

  private leastOccludedFocusDirection(
    bounds: THREE.Box3,
    samples: readonly FocusSample[]
  ): { readonly direction: THREE.Vector3; readonly visibleSamples: number; readonly candidateCount: number } {
    const center = bounds.getCenter(new THREE.Vector3());
    const size = bounds.getSize(new THREE.Vector3());
    const span = Math.max(size.x, size.y, size.z, 4);
    const verticalFov = THREE.MathUtils.degToRad(this.camera.fov);
    const horizontalFov = 2 * Math.atan(Math.tan(verticalFov / 2) * Math.max(this.camera.aspect, 0.1));
    const fitFov = Math.max(0.1, Math.min(verticalFov, horizontalFov));
    const distance = Math.max(1, (span * 0.62) / Math.tan(fitFov / 2));
    const currentDirection = this.camera.position.clone().sub(this.controls.target);
    if (currentDirection.lengthSq() === 0) currentDirection.set(0.85, 0.75, 1.1);
    currentDirection.normalize();
    const baseAzimuth = Math.atan2(currentDirection.z, currentDirection.x);
    const elevation = Math.max(0.35, Math.min(0.85, Math.abs(currentDirection.y)));
    const candidates = [currentDirection.clone()];
    for (const candidateElevation of [elevation, 0.62]) {
      for (const azimuthOffset of [0, Math.PI / 4, -Math.PI / 4, Math.PI / 2, -Math.PI / 2, Math.PI]) {
        const azimuth = baseAzimuth + azimuthOffset;
        const direction = new THREE.Vector3(Math.cos(azimuth), candidateElevation, Math.sin(azimuth)).normalize();
        if (!candidates.some((candidate) => candidate.dot(direction) > 0.999)) candidates.push(direction);
      }
    }
    let bestDirection = candidates[0].clone();
    let bestVisible = this.visibleFocusSamples(center.clone().addScaledVector(bestDirection, distance), samples);
    let bestCloseness = bestDirection.dot(currentDirection);
    for (const direction of candidates.slice(1)) {
      const visible = this.visibleFocusSamples(center.clone().addScaledVector(direction, distance), samples);
      const closeness = direction.dot(currentDirection);
      if (visible > bestVisible || (visible === bestVisible && closeness > bestCloseness)) {
        bestDirection = direction.clone();
        bestVisible = visible;
        bestCloseness = closeness;
      }
    }
    return { direction: bestDirection, visibleSamples: bestVisible, candidateCount: candidates.length };
  }

  private visibleFocusSamples(cameraPosition: THREE.Vector3, samples: readonly FocusSample[]): number {
    if ([...this.meshBounds.values()].some((bounds) => bounds.containsPoint(cameraPosition))) return -1;
    const ray = new THREE.Ray();
    const direction = new THREE.Vector3();
    const intersection = new THREE.Vector3();
    let visible = 0;
    for (const sample of samples) {
      direction.copy(sample.point).sub(cameraPosition);
      const sampleDistance = direction.length();
      if (sampleDistance === 0) continue;
      ray.set(cameraPosition, direction.normalize());
      let nearestDistance = Number.POSITIVE_INFINITY;
      let nearestId: string | null = null;
      for (const [id, meshBounds] of this.meshBounds) {
        const hit = ray.intersectBox(meshBounds, intersection);
        if (hit === null) continue;
        const distance = cameraPosition.distanceTo(hit);
        if (distance <= sampleDistance + Math.max(0.0001, sampleDistance * 0.0001) && distance < nearestDistance) {
          nearestDistance = distance;
          nearestId = id;
        }
      }
      if (nearestId === sample.elementId) visible += 1;
    }
    return visible;
  }

  private transitionCamera(cameraPosition: THREE.Vector3, target: THREE.Vector3): void {
    this.cancelCameraTransition();
    const startedAt = performance.now();
    const startCamera = this.camera.position.clone();
    const startTarget = this.controls.target.clone();
    this.transitioning = true;
    const step = (now: number): void => {
      const progress = Math.min(1, Math.max(0, (now - startedAt) / cameraTransitionMs));
      const eased = 1 - Math.pow(1 - progress, 3);
      this.camera.position.lerpVectors(startCamera, cameraPosition, eased);
      this.controls.target.lerpVectors(startTarget, target, eased);
      this.camera.lookAt(this.controls.target);
      this.updateClipping();
      this.render();
      if (progress < 1) {
        this.transitionFrame = requestAnimationFrame(step);
        return;
      }
      this.transitionFrame = null;
      this.transitioning = false;
      this.controls.update();
      this.render();
    };
    this.transitionFrame = requestAnimationFrame(step);
  }

  private readonly cancelCameraTransition = (): void => {
    if (this.transitionFrame !== null) cancelAnimationFrame(this.transitionFrame);
    this.transitionFrame = null;
    this.transitioning = false;
  };

  private elementAt(event: MouseEvent | PointerEvent): string | null {
    const bounds = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
    this.pointer.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const hit = this.raycaster.intersectObjects([...this.meshes.values()], false)[0];
    return hit === undefined ? null : (hit.object as SelectableMesh).userData.elementId;
  }

  private readonly focusPick = (event: MouseEvent): void => {
    if (this.interactionMode === "select") return;
    const id = this.elementAt(event);
    if (id !== null) this.onFocus(id);
  };

  private readonly moveCameraThroughScene = (event: WheelEvent): void => {
    event.preventDefault();
    event.stopImmediatePropagation();
    this.cancelCameraTransition();
    const bounds = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - bounds.left) / Math.max(1, bounds.width)) * 2 - 1;
    this.pointer.y = -((event.clientY - bounds.top) / Math.max(1, bounds.height)) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const deltaPixels = event.deltaMode === WheelEvent.DOM_DELTA_LINE
      ? event.deltaY * 16
      : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
        ? event.deltaY * Math.max(1, bounds.height)
        : event.deltaY;
    const steps = THREE.MathUtils.clamp(-deltaPixels / 100, -3, 3);
    if (steps === 0) return;
    const translation = this.raycaster.ray.direction.clone().multiplyScalar(this.sceneSpan * cameraTravelPerWheelStep * steps);
    this.camera.position.add(translation);
    this.controls.target.add(translation);
    this.updateClipping();
    this.controls.update();
    this.render();
  };

  private readonly startWindowSelection = (event: PointerEvent): void => {
    if (this.interactionMode !== "select" || event.button !== 0) return;
    const point = this.localPointer(event);
    const previous = this.lastSelectClick;
    const doublePress = previous !== null &&
      performance.now() - previous.at <= selectDoublePressMs &&
      Math.hypot(point.x - previous.x, point.y - previous.y) <= selectDoublePressRadius;
    if (doublePress) {
      this.lastSelectClick = null;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.selectPanStart = { ...point, pointerId: event.pointerId };
      if (event.isTrusted) this.renderer.domElement.setPointerCapture(event.pointerId);
      return;
    }
    this.lastSelectClick = null;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.windowSelectionStart = { ...point, pointerId: event.pointerId };
    if (event.isTrusted) this.renderer.domElement.setPointerCapture(event.pointerId);
    this.updateSelectionOverlay(point.x, point.y, point.x, point.y);
  };

  private readonly moveWindowSelection = (event: PointerEvent): void => {
    const panStart = this.selectPanStart;
    if (panStart !== null && panStart.pointerId === event.pointerId) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const point = this.localPointer(event);
      const scale = this.sceneSpan * 1.5 / Math.max(1, this.renderer.domElement.clientHeight);
      const translation = new THREE.Vector3()
        .setFromMatrixColumn(this.camera.matrix, 0)
        .multiplyScalar(-(point.x - panStart.x) * scale)
        .add(new THREE.Vector3()
          .setFromMatrixColumn(this.camera.matrix, 1)
          .multiplyScalar((point.y - panStart.y) * scale));
      this.camera.position.add(translation);
      this.controls.target.add(translation);
      panStart.x = point.x;
      panStart.y = point.y;
      this.updateClipping();
      this.controls.update();
      this.render();
      return;
    }
    const start = this.windowSelectionStart;
    if (start === null || start.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const point = this.localPointer(event);
    this.updateSelectionOverlay(start.x, start.y, point.x, point.y);
  };

  private readonly finishWindowSelection = (event: PointerEvent): void => {
    const panStart = this.selectPanStart;
    if (panStart !== null && panStart.pointerId === event.pointerId) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.cancelWindowSelection();
      return;
    }
    const start = this.windowSelectionStart;
    if (start === null || start.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const end = this.localPointer(event);
    const dragDistance = Math.hypot(end.x - start.x, end.y - start.y);
    if (dragDistance < 4) {
      const id = this.elementAt(event);
      this.cancelWindowSelection();
      this.lastSelectClick = { ...end, at: performance.now() };
      this.onRangeSelect(id === null ? new Set() : new Set([id]), { additive: true });
      return;
    }
    this.lastSelectClick = null;
    const minX = Math.min(start.x, end.x);
    const maxX = Math.max(start.x, end.x);
    const minY = Math.min(start.y, end.y);
    const maxY = Math.max(start.y, end.y);
    const selected = new Set<string>();
    const width = Math.max(1, this.renderer.domElement.clientWidth);
    const height = Math.max(1, this.renderer.domElement.clientHeight);
    const position = new THREE.Vector3();
    for (const [id, mesh] of this.meshes) {
      mesh.getWorldPosition(position).project(this.camera);
      if (position.z < -1 || position.z > 1) continue;
      const screenX = ((position.x + 1) / 2) * width;
      const screenY = ((1 - position.y) / 2) * height;
      if (screenX >= minX && screenX <= maxX && screenY >= minY && screenY <= maxY) selected.add(id);
    }
    this.cancelWindowSelection();
    this.onRangeSelect(selected, { additive: event.metaKey || event.ctrlKey });
  };

  private readonly cancelWindowSelection = (): void => {
    const start = this.windowSelectionStart;
    if (start !== null && this.renderer.domElement.hasPointerCapture(start.pointerId)) {
      this.renderer.domElement.releasePointerCapture(start.pointerId);
    }
    const panStart = this.selectPanStart;
    if (panStart !== null && this.renderer.domElement.hasPointerCapture(panStart.pointerId)) {
      this.renderer.domElement.releasePointerCapture(panStart.pointerId);
    }
    this.windowSelectionStart = null;
    this.selectPanStart = null;
    this.selectionOverlay.hidden = true;
  };

  private localPointer(event: PointerEvent): { readonly x: number; readonly y: number } {
    const bounds = this.renderer.domElement.getBoundingClientRect();
    return {
      x: Math.min(bounds.width, Math.max(0, event.clientX - bounds.left)),
      y: Math.min(bounds.height, Math.max(0, event.clientY - bounds.top))
    };
  }

  private updateSelectionOverlay(startX: number, startY: number, endX: number, endY: number): void {
    const left = Math.min(startX, endX);
    const top = Math.min(startY, endY);
    this.selectionOverlay.hidden = false;
    this.selectionOverlay.style.left = `${left}px`;
    this.selectionOverlay.style.top = `${top}px`;
    this.selectionOverlay.style.width = `${Math.abs(endX - startX)}px`;
    this.selectionOverlay.style.height = `${Math.abs(endY - startY)}px`;
  }

  private resize(): void {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height, false);
    this.render();
  }

  private updateClipping(): void {
    const distance = Math.max(this.camera.position.distanceTo(this.controls.target), 0.01);
    this.camera.near = Math.max(0.005, Math.min(distance / 1_000, this.sceneSpan / 10_000));
    this.camera.far = Math.max(200, distance + this.sceneSpan * 50);
    this.camera.updateProjectionMatrix();
  }

  private readonly handleControlsChange = (): void => {
    this.updateClipping();
    this.render();
  };

  private readonly render = (): void => {
    this.renderer.render(this.scene, this.camera);
  };
}
